import { useEffect, type RefObject } from 'react';

interface Options {
  /** Whether more pages are available to fetch. */
  hasNextPage: boolean;
  /** Whether a next-page fetch is currently in flight. */
  isFetchingNextPage: boolean;
  /** Called when the sentinel intersects the scroll root. */
  fetchNextPage: () => void;
  /** Optional scroll container — when omitted, the viewport is used. */
  rootRef?: RefObject<HTMLElement | null>;
  /**
   * Trigger a fetch when the sentinel is this many pixels away from the
   * scroll root's bottom edge. Defaults to 200px so the next page arrives
   * before the user actually hits the bottom — prevents the visible "wait
   * for spinner" beat.
   */
  rootMarginPx?: number;
}

/**
 * Wires an IntersectionObserver to a sentinel element and triggers
 * `fetchNextPage` whenever the sentinel comes into view. Idempotent — if
 * a fetch is already in flight or there are no more pages, the observer
 * stays bound but won't dispatch redundant calls.
 *
 * Resilient to environments without `IntersectionObserver` (e.g. some
 * Node-based test runners) — the hook quietly no-ops if the API isn't
 * available, leaving the user with a static first page rather than a crash.
 */
export function useInfiniteScrollSentinel(
  sentinelRef: RefObject<HTMLElement | null>,
  {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef,
    rootMarginPx = 200,
  }: Options,
): void {
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (!hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (isFetchingNextPage) return;
        fetchNextPage();
      },
      {
        root: rootRef?.current ?? null,
        rootMargin: `0px 0px ${rootMarginPx}px 0px`,
        threshold: 0,
      },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    sentinelRef,
    rootRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootMarginPx,
  ]);
}
